import { useState } from 'react';

import { Check } from 'lucide-react';
import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { Logout } from '@/components/shared/Logout';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import {
  apiCreateVault,
  apiImportVault,
  apiVerifyVault,
} from '@/services/vault.api';
import { useUserStore } from '@/store';
import { invalidateQuery } from '@/lib/hooks/useQuery';

const PLATFORM_ID = import.meta.env.VITE_PLATFORM_ID;

const VaultDialog = ({ isOpen, onVaultConnected }) => {
  const { setVaultId,user } = useUserStore();
  const [dialogState, setDialogState] = useState('connect');
  const [email, setEmail] = useState(user?.email as string ||'');
  const [vaultIdInput, setVaultIdInput] = useState('');
  const [OTP, setOTP] = useState<string>('');


  const { mutateAsync: createVault, isLoading: createVaultLoading } = useMutation({
    mutationFn: apiCreateVault,
    onSuccess: () => {
      setDialogState('createOtp');
    },
    onError: (error: any) => {
      toast.error(
        String(error?.response?.data?.message)
      );
    },
  });
  const { mutateAsync: importVault, isLoading: importVaultLoading } = useMutation({
    mutationFn: apiImportVault,
    onSuccess: (res) => {
      setDialogState('otpVerify');
      setEmail(res?.data?.data?.email);
      if (res?.data?.data?.vaultId) {
        setVaultId(res?.data?.data?.vaultId);
      }
    },
    onError: (error: any) => {
      toast.error(
        String(error?.response?.data?.message)
      );
    },
  });
  const { mutateAsync: verifyOTP, isLoading: verifyOTPLoading } = useMutation({
    mutationFn: apiVerifyVault,
    onSuccess: (res) => {
      invalidateQuery(['profile'])
      toast.success(String(res?.data?.message));
      setVaultId(res?.data?.data?.vaultId);
      setDialogState('success');
    },
    onError: (error: any) => {
      toast.error(
        String(error?.response?.data?.message)
      );
    },
  });

  const handleConnect = async (e) => {
    e.preventDefault();
    const payload = {
      vaultId: vaultIdInput,
      platformId: PLATFORM_ID,
    };
    await importVault(payload);
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const payload = {
      email: email,
      otp: OTP,
    };
    await verifyOTP(payload);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = {
      email: email,
      platformId: PLATFORM_ID,
    };
    await createVault(payload);
  };

  const handleSuccess = () => {
    if (vaultIdInput) {
      setVaultId(vaultIdInput);
    }
    onVaultConnected();
  };

  const renderDialogContent = () => {
    switch (dialogState) {
      case 'connect':
        return (
          <DialogContent
            className="max-w-md min-h-96 p-9 flex flex-col"
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-center">
                Connect to Twin Vault
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleConnect}
              className="space-y-4 w-full p-4 flex-1"
            >
              <p className="font-semibold text-black text-left">
                Enter Vault ID
              </p>
              <div className="space-y-2">
                <Input
                  placeholder="Ex: bf1c8b58ce0859f3a.........."
                  value={vaultIdInput}
                  onChange={(e) => setVaultIdInput(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" variant="default" disabled={importVaultLoading}>
                Connect Vault
              </Button>
              <div className="text-center text-sm">
                Don't Have Vault?{' '}
                <button
                  type="button"
                  className="font-semibold text-primary hover:underline"
                  onClick={() => setDialogState('create')}
                >
                  Create Now
                </button>
              </div>
            </form>
            <div className="text-center mt-auto pb-4">
              <Logout>
                <button
                  type="button"
                  className="font-semibold text-destructive hover:underline"
                >
                  Logout
                </button>
              </Logout>
            </div>
          </DialogContent>
        );

      case 'otpVerify':
        return (
          <DialogContent
            className="max-w-md min-h-96 p-9 flex flex-col"
            showCloseButton={false}
          >
            <DialogHeader className="text-center">
              <DialogTitle className="font-bold text-2xl text-center">
                OTP Verification
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleOtpVerify}
              className="space-y-4 flex flex-col items-center w-full p-4 flex-1"
            >
              <p className="text-sm text-gray-500 text-center">
                We have sent a verification code to your registered email ID.
                <br />
                <span className="text-gray-700">{email}</span>
              </p>

              <div className="py-2">
                <InputOTP
                  maxLength={6}
                  value={OTP}
                  onChange={(value: string) => setOTP(value)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button type="submit" className="w-full" variant="default" disabled={verifyOTPLoading}>
                Submit
              </Button>
            </form>
            <div className="text-center mt-auto pb-4">
              Back to{' '}
              <button
                type="button"
                className="font-semibold text-primary hover:underline"
                onClick={() => setDialogState('connect')}
              >
                Connect Vault
              </button>
            </div>
          </DialogContent>
        );

      case 'create':
        return (
          <DialogContent
            className="max-w-md min-h-96 p-9 flex flex-col"
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-center">
                Create New Vault ID
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleCreate}
              className="space-y-4 w-full p-4 flex-1"
            >
              <p className="font-semibold text-black text-left">
                Enter Email Address
              </p>
              <div className="space-y-2">
                <Input
                  placeholder="Ex: example.vault@gmail.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" variant="default" disabled={createVaultLoading}>
                Sign Up
              </Button>
              <div className="text-center text-sm">
                Already have account?{' '}
                <button
                  type="button"
                  className="text-primary font-semibold hover:underline"
                  onClick={() => setDialogState('connect')}
                >
                  Login
                </button>
              </div>
            </form>
            <div className="text-center mt-auto pb-4">
              <Logout>
                <button
                  type="button"
                  className="font-semibold text-destructive hover:underline"
                >
                  Logout
                </button>
              </Logout>
            </div>
          </DialogContent>
        );

      case 'createOtp':
        return (
          <DialogContent
            className="max-w-md min-h-96 p-9 flex flex-col"
            showCloseButton={false}
          >
            <DialogHeader className="text-center">
              <DialogTitle className="font-bold text-2xl text-center">
                OTP Verification
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleOtpVerify}
              className="space-y-4 flex flex-col items-center w-full p-4 flex-1"
            >
              <p className="text-sm text-gray-500 text-center">
                We have sent a verification code to your registered email
                <br />
                <span className="text-gray-700">{email}</span>
              </p>

              <div className="py-2">
                <InputOTP
                  maxLength={6}
                  value={OTP}
                  onChange={(value: string) => setOTP(value)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button type="submit" className="w-full" variant="default" disabled={verifyOTPLoading}>
                Submit
              </Button>
            </form>
            <div className="text-center mt-auto pb-4">
              Back to{' '}
              <button
                type="button"
                className="text-primary font-semibold hover:underline"
                onClick={() => setDialogState('create')}
              >
                Create Vault
              </button>
            </div>
          </DialogContent>
        );

      case 'success':
        return (
          <DialogContent
            className="max-w-md min-h-96 p-9 flex flex-col"
            showCloseButton={false}
          >
            {/* <DialogHeader className="text-center">
              <DialogTitle className="font-bold text-2xl text-center">Success!</DialogTitle>
            </DialogHeader> */}
            <div className="flex flex-col items-center space-y-4 py-4 flex-1">
              <div className="rounded-full bg-green-500 p-2">
                <Check className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-xl font-semibold">Congratulations!</h2>
              <p className="text-sm text-gray-500">
                Your account created successfully
                <br />
                <span className="text-gray-700">
                  We have sent vault ID to your email
                </span>
                <br />
                <span className="text-gray-700">{email}</span>
              </p>
            </div>
            <Button
              onClick={handleSuccess}
              className="w-full mt-auto mb-4"
              variant="default"
            >
              Continue
            </Button>
          </DialogContent>
        );

      default:
        return null;
    }
  };

  return <Dialog open={isOpen}>{renderDialogContent()}</Dialog>;
};

export default VaultDialog;